import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { request } from 'js/api/axios';
import { LoadingMessage, Icon } from 'js/components';
import { RadioOption, RadioOptionGroup } from 'js/components/Forms/RadioOption';
import { download as downloadFile } from 'js/actions/file-actions';
import { allowAiAnalysis } from 'js/utils/entitlement-utils';

import AnalysisUnitsGauge from './AnalysisUnitsGauge';
import ChatComposer from './ChatComposer';
import { DraftCard, MessageActions, MessageParts } from './ChatTurn';
import { draftPartOf, latestChatPrompts, readerLocale } from './chat-helpers';
import {
  ANALYSIS_ERRORS,
  friendlyReason,
  requestErrorMessage,
} from './analysis-errors';
import RefineQuestions from './RefineQuestions';
import EmptyAnalysisIcon from './EmptyAnalysisIcon';
import { watchGenerationRecord } from './generation-record';
import OrgBriefing, { ChatMarkdown, ThinkingIndicator } from './OrgBriefing';
import styles from './OrgAnalyze.module.scss';
import {
  joinOrgAnalysisChannel,
  orgAnalysisTopic,
  randomNonce,
} from './org-analysis-channel';
import { faCloudArrowDown } from '@fortawesome/pro-regular-svg-icons';
import { faRobot } from '@fortawesome/pro-solid-svg-icons';

export default function OrgAnalyze({
  workspace,
  organization,
  assessmentKey,
  isRubric = false,
  assessmentTemplate = null,
}) {
  const token = useSelector((state) => state.identity.token);
  const currentUserId = useSelector((state) => state.identity.me?.id);
  const activeAccount = useSelector((state) => state.activeAccount);
  const canAnalyze = allowAiAnalysis(activeAccount);
  const [status, setStatus] = useState(null);
  const [conversation, setConversation] = useState({ messages: [] });
  const [loading, setLoading] = useState(canAnalyze);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateProgress, setGenerateProgress] = useState(null);
  // The briefing as it is being written, from generate_partial. Replaced by
  // the finished briefing on completion; cleared on error.
  const [partialBriefing, setPartialBriefing] = useState(null);
  const [sending, setSending] = useState(false);
  // Drafts created from chat. draftCache holds full content once known (from
  // a live completion or a lazy fetch); expandedDrafts tracks which cards the
  // reader opened; liveGenerations tracks in-progress chat-triggered
  // generations by nonce, so a "generation" marker on a message can show
  // real progress instead of a static "starting…".
  const [draftCache, setDraftCache] = useState({});
  const [expandedDrafts, setExpandedDrafts] = useState(() => new Set());
  const [liveGenerations, setLiveGenerations] = useState({});
  const [promotingId, setPromotingId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [bundlePromptOpen, setBundlePromptOpen] = useState(false);
  const [bundleIncludeConversation, setBundleIncludeConversation] =
    useState(false);
  const dispatch = useDispatch();
  const [viewedId, setViewedId] = useState(null);
  const watchedNoncesRef = useRef(new Set());
  const composerRef = useRef(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const basePath = `/api/workspaces/${workspace.id}/organizations/${
    organization.id
  }/analysis/${encodeURIComponent(assessmentKey)}`;

  const query = useMemo(() => {
    const params = {};
    if (isRubric) {
      params.source_type = 'rubric';
    }
    return params;
  }, [isRubric]);

  const downloadHref = useMemo(() => {
    if (!workspace?.id || !organization?.id || !assessmentKey) {
      return null;
    }

    const params = new URLSearchParams({
      workspace_id: String(workspace.id),
      organization_id: String(organization.id),
      source_key: assessmentKey,
    });

    if (isRubric) {
      params.set('source_type', 'rubric');
    }

    return `/reports/org-results-pack?${params.toString()}`;
  }, [assessmentKey, isRubric, organization?.id, workspace?.id]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [statusResp, convResp] = await Promise.all([
        request.get(basePath, { headers, params: query }),
        request.get(`${basePath}/conversation`, { headers, params: query }),
      ]);
      const nextStatus = statusResp.data?.data;
      setStatus(nextStatus);
      setConversation(convResp.data?.data ?? { messages: [] });
    } catch (e) {
      setError(requestErrorMessage(e, ANALYSIS_ERRORS.load));
    } finally {
      setLoading(false);
    }
  }, [basePath, headers, query]);

  useEffect(() => {
    if (!canAnalyze) {
      setLoading(false);
      return;
    }

    load();
  }, [canAnalyze, load]);

  // Best-effort re-fetch of just the conversation. Used after a chat-triggered
  // draft finishes, and when this tab is focused again so work from another
  // tab lands here without a full page reload.
  const refreshStatus = useCallback(async () => {
    try {
      const resp = await request.get(basePath, { headers, params: query });
      const next = resp.data?.data;
      if (next) {
        setStatus(next);
      }
    } catch {
      // The current briefing is still on screen; versions can wait.
    }
  }, [basePath, headers, query]);

  const refreshConversation = useCallback(async () => {
    try {
      const resp = await request.get(`${basePath}/conversation`, {
        headers,
        params: query,
      });
      setConversation(resp.data?.data ?? { messages: [] });
    } catch (e) {
      // The channel already told the reader what happened; a failed refresh
      // here just means they see it on next reload instead.
    }
  }, [basePath, headers, query]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshConversation();
      }
    };

    window.addEventListener('focus', refreshConversation);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refreshConversation);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refreshConversation]);

  const generateChannelRef = useRef(null);
  // Stops for the database checks on attempts still being watched, so none
  // keeps running after the panel is gone.
  const recordStopsRef = useRef(new Map());
  const generateCancelledRef = useRef(false);

  useEffect(() => {
    generateCancelledRef.current = false;
    const recordStops = recordStopsRef.current;
    return () => {
      generateCancelledRef.current = true;
      generateChannelRef.current?.leave();
      generateChannelRef.current = null;
      recordStops.forEach((stop) => stop());
      recordStops.clear();
    };
  }, []);

  // Watches one shared generate: the channel for progress and the result,
  // and the attempt's database record as the fallback. The record settles it
  // when the channel cannot -- the node holding its progress died, or the
  // socket dropped past the terminal event. Used for a new generate and for
  // one a reload found still running.
  const watchGenerate = useCallback(
    async (generation) => {
      const { nonce, topic } = generation;
      let settled = false;
      let stopRecord = () => {};
      const generatingNow = () => !generateCancelledRef.current && !settled;

      setGenerating(true);
      setError(null);
      setGenerateProgress((prev) => prev || 'queued');

      const settle = () => {
        settled = true;
        stopRecord();
        generateChannelRef.current?.leave();
        generateChannelRef.current = null;
      };

      const stopGenerating = (message) => {
        if (!generatingNow()) {
          return;
        }
        settle();
        setGenerating(false);
        setGenerateProgress(null);
        setPartialBriefing(null);
        if (message) {
          setError(message);
        }
      };

      const finish = (briefing) => {
        if (!generatingNow()) {
          return;
        }
        settle();
        setGenerating(false);
        setGenerateProgress(null);
        setPartialBriefing(null);
        setError(null);
        if (briefing) {
          setStatus((prev) => ({ ...(prev ?? {}), briefing, stale: false }));
        }
        setViewedId(briefing?.id ?? null);
        refreshStatus();
      };

      const handlers = {
        onProgress: (payload) => {
          if (generatingNow()) {
            setGenerateProgress(payload?.stage || null);
          }
        },
        onPartial: (payload) => {
          if (generatingNow() && payload?.document) {
            setPartialBriefing(payload.document);
          }
        },
        onComplete: (payload) => {
          if (generatingNow() && payload?.usage) {
            setStatus((prev) => ({ ...(prev ?? {}), usage: payload.usage }));
          }
          finish(payload?.briefing);
        },
        onError: (payload) => {
          stopGenerating(
            friendlyReason(payload?.reason, ANALYSIS_ERRORS.generateFailed)
          );
        },
      };

      const stopWatching = watchGenerationRecord({
        basePath,
        headers,
        nonce,
        onSettled: (record) => {
          if (record.status === 'complete') {
            finish(record.briefing_id ? { id: record.briefing_id } : null);
          } else {
            stopGenerating(
              friendlyReason(record.error, ANALYSIS_ERRORS.generateFailed)
            );
          }
        },
      });
      recordStopsRef.current.set(nonce, stopWatching);
      stopRecord = () => {
        stopWatching();
        recordStopsRef.current.delete(nonce);
      };

      try {
        // The server signs who may watch this nonce; any node can verify it.
        const joined = await joinOrgAnalysisChannel(token, topic, handlers, {
          workspace_id: workspace.id,
          token: generation.token,
        });
        if (!generatingNow()) {
          joined?.leave();
          return;
        }
        generateChannelRef.current = joined;
      } catch (e) {
        // The record check keeps running: the work may still finish.
      }
    },
    [basePath, headers, refreshStatus, token, workspace.id]
  );

  const generate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    setGenerateProgress('queued');
    setPartialBriefing(null);

    const nonce = randomNonce();

    try {
      const started = await request.post(
        basePath,
        { nonce, ...query, locale: readerLocale() },
        { headers }
      );
      if (generateCancelledRef.current) {
        return;
      }
      watchGenerate({
        nonce,
        topic: orgAnalysisTopic(nonce),
        token: started.data?.data?.join_token,
      });
    } catch (e) {
      setGenerating(false);
      setGenerateProgress(null);
      setError(requestErrorMessage(e, ANALYSIS_ERRORS.generate));
    }
  }, [basePath, headers, query, watchGenerate]);

  // A reload mid-generate: status names the reader's own generate still
  // running, so pick it back up rather than offering Generate again.
  const resumedNonceRef = useRef(null);
  useEffect(() => {
    const running = status?.generation;
    if (!running?.nonce || resumedNonceRef.current === running.nonce) {
      return;
    }
    resumedNonceRef.current = running.nonce;
    if (!generating) {
      watchGenerate(running);
    }
  }, [status?.generation, generating, watchGenerate]);

  // Watches one chat-triggered draft's background generation. The chat POST
  // already returned (with a "generation" marker on the reply); this just
  // joins the channel it named and reacts as progress/completion arrive.
  const watchDraftGeneration = useCallback(
    (generation) => {
      if (!generation?.nonce || !generation?.topic) {
        return;
      }
      if (watchedNoncesRef.current.has(generation.nonce)) {
        return;
      }
      watchedNoncesRef.current.add(generation.nonce);

      setLiveGenerations((prev) => ({
        ...prev,
        [generation.nonce]: { stage: 'queued' },
      }));

      const handlers = {
        onProgress: (payload) =>
          setLiveGenerations((prev) => ({
            ...prev,
            [generation.nonce]: {
              ...prev[generation.nonce],
              stage: payload?.stage || null,
            },
          })),
        // A chat reply as it is written. Each event is the whole reply so
        // far; a new model call (after a tool round) starts it over.
        onStream: (payload) =>
          setLiveGenerations((prev) => ({
            ...prev,
            [generation.nonce]: {
              ...prev[generation.nonce],
              text: payload?.text ?? '',
            },
          })),
        // A chat-triggered draft briefing as it is written.
        onPartial: (payload) =>
          setLiveGenerations((prev) => ({
            ...prev,
            [generation.nonce]: {
              ...prev[generation.nonce],
              partial: payload?.document ?? null,
            },
          })),
        onComplete: (payload) => {
          setSending(false);
          setLiveGenerations((prev) => {
            const next = { ...prev };
            delete next[generation.nonce];
            return next;
          });
          if (payload?.conversation) {
            setConversation(payload.conversation);
            setStatus((prev) =>
              prev ? { ...prev, usage: payload.usage ?? prev.usage } : prev
            );
          }
          if (payload?.briefing?.id) {
            setDraftCache((prev) => ({
              ...prev,
              [payload.briefing.id]: payload.briefing,
            }));
            setExpandedDrafts((prev) => new Set(prev).add(payload.briefing.id));
            refreshStatus();
          }
          if (!payload?.conversation) {
            refreshConversation();
          }
        },
        onError: (payload) => {
          setSending(false);
          setLiveGenerations((prev) => ({
            ...prev,
            [generation.nonce]: {
              error: friendlyReason(
                payload?.reason,
                ANALYSIS_ERRORS.draftFailed
              ),
            },
          }));
          refreshConversation();
        },
      };

      // Whichever settles first -- the channel or the attempt's database
      // record -- stops the other.
      const stopRecord = () => {
        recordStopsRef.current.get(generation.nonce)?.();
        recordStopsRef.current.delete(generation.nonce);
      };
      const channelComplete = handlers.onComplete;
      const channelError = handlers.onError;
      handlers.onComplete = (payload) => {
        stopRecord();
        channelComplete(payload);
      };
      handlers.onError = (payload) => {
        stopRecord();
        channelError(payload);
      };

      recordStopsRef.current.set(
        generation.nonce,
        watchGenerationRecord({
          basePath,
          headers,
          nonce: generation.nonce,
          onSettled: (record) => {
            recordStopsRef.current.delete(generation.nonce);
            if (record.status === 'complete') {
              if (record.briefing_id) {
                setExpandedDrafts((prev) =>
                  new Set(prev).add(record.briefing_id)
                );
                refreshStatus();
              }
              channelComplete({});
            } else {
              channelError({ reason: record.error });
            }
          },
        })
      );

      joinOrgAnalysisChannel(token, generation.topic, handlers, {
        workspace_id: workspace.id,
        token: generation.token,
      }).catch(() => {
        watchedNoncesRef.current.delete(generation.nonce);
        setSending(false);
        setLiveGenerations((prev) => ({
          ...prev,
          [generation.nonce]: {
            error: ANALYSIS_ERRORS.updates,
          },
        }));
      });
    },
    [basePath, headers, refreshConversation, refreshStatus, token, workspace.id]
  );

  // Other tabs do not share this socket. After a focus/visibility refetch,
  // join any in-flight draft so this tab can show progress instead of a
  // static "generating…" card until the next refresh.
  useEffect(() => {
    (conversation?.messages ?? []).forEach((item) => {
      const generating =
        item.status === 'generating' ||
        draftPartOf(item)?.ref?.generation_status === 'generating';
      if (generating && item.generation) {
        watchDraftGeneration(item.generation);
      }
    });
  }, [conversation, watchDraftGeneration]);

  const regenerateResponse = useCallback(
    async (messageId) => {
      if (!messageId || String(messageId).startsWith('local-')) {
        return;
      }

      setRegeneratingId(messageId);
      setError(null);

      const nonce = randomNonce();
      const topic = orgAnalysisTopic(nonce);

      try {
        const resp = await request.post(
          `${basePath}/conversation/${messageId}/regenerate`,
          { nonce, ...query, locale: readerLocale() },
          { headers }
        );
        const started = resp.data?.data;
        if (started?.conversation) {
          setConversation(started.conversation);
        }
        setStatus((prev) =>
          prev ? { ...prev, usage: started?.usage ?? prev.usage } : prev
        );
        watchDraftGeneration(
          started?.message?.generation || {
            nonce,
            topic,
            token: started?.join_token,
          }
        );
      } catch (e) {
        await refreshConversation();
        setError(requestErrorMessage(e, ANALYSIS_ERRORS.regenerateReply));
      } finally {
        setRegeneratingId(null);
      }
    },
    [basePath, headers, query, refreshConversation, watchDraftGeneration]
  );

  const requestBriefingUpdate = useCallback(async () => {
    setSending(true);
    setError(null);

    const nonce = randomNonce();
    const topic = orgAnalysisTopic(nonce);

    try {
      const resp = await request.post(
        `${basePath}/conversation/update-briefing`,
        { nonce, ...query, locale: readerLocale() },
        { headers }
      );
      const started = resp.data?.data;
      if (started?.conversation) {
        setConversation(started.conversation);
      }
      setStatus((prev) =>
        prev ? { ...prev, usage: started?.usage ?? prev.usage } : prev
      );
      watchDraftGeneration(
        started?.message?.generation || {
          nonce,
          topic,
          token: started?.join_token,
        }
      );
    } catch (e) {
      await refreshConversation();
      setError(requestErrorMessage(e, ANALYSIS_ERRORS.briefingUpdate));
      setSending(false);
    }
  }, [basePath, headers, query, refreshConversation, watchDraftGeneration]);

  const sendMessage = useCallback(
    async (raw) => {
      const content = (raw || '').trim();
      if (!content) {
        return;
      }

      setSending(true);
      setError(null);

      const nonce = randomNonce();
      const topic = orgAnalysisTopic(nonce);

      // Echo the reader's own message immediately rather than waiting on the
      // round trip -- the assistant's reply arrives over the nonce channel.
      const optimistic = {
        id: `local-${Date.now()}`,
        role: 'user',
        content,
        inserted_at: new Date().toISOString(),
      };
      setConversation((prev) => ({
        ...(prev ?? {}),
        messages: [...(prev?.messages ?? []), optimistic],
      }));

      try {
        const resp = await request.post(
          `${basePath}/conversation`,
          { content, nonce, ...query, locale: readerLocale() },
          { headers }
        );
        const started = resp.data?.data;
        if (started?.conversation) {
          setConversation(started.conversation);
        }
        setStatus((prev) =>
          prev ? { ...prev, usage: started?.usage ?? prev.usage } : prev
        );

        const generation = started?.message?.generation || {
          nonce,
          topic,
          token: started?.join_token,
        };
        watchDraftGeneration(generation);
      } catch (e) {
        await refreshConversation();
        setError(requestErrorMessage(e, ANALYSIS_ERRORS.followUp));
        setSending(false);
      }
    },
    [basePath, headers, query, refreshConversation, watchDraftGeneration]
  );

  const expandDraft = useCallback(
    async (briefingId) => {
      setExpandedDrafts((prev) => new Set(prev).add(briefingId));
      if (draftCache[briefingId]) {
        return;
      }
      try {
        const resp = await request.get(`${basePath}/drafts/${briefingId}`, {
          headers,
          params: query,
        });
        const draft = resp.data?.data?.briefing;
        if (draft) {
          setDraftCache((prev) => ({ ...prev, [briefingId]: draft }));
        }
      } catch (e) {
        setError(requestErrorMessage(e, ANALYSIS_ERRORS.loadDraft));
      }
    },
    [basePath, draftCache, headers, query]
  );

  const promoteDraft = useCallback(
    async (briefingId) => {
      setPromotingId(briefingId);
      setError(null);
      try {
        const resp = await request.post(
          `${basePath}/drafts/${briefingId}/promote`,
          query,
          { headers }
        );
        const promoted = resp.data?.data?.briefing;
        if (promoted) {
          setDraftCache((prev) => ({ ...prev, [briefingId]: promoted }));
          setStatus((prev) => ({
            ...(prev ?? {}),
            briefing: promoted,
            stale: false,
          }));
          setViewedId(promoted.id);
          refreshStatus();
        }
      } catch (e) {
        setError(requestErrorMessage(e, ANALYSIS_ERRORS.publishDraft));
      } finally {
        setPromotingId(null);
      }
    },
    [basePath, headers, query, refreshStatus]
  );

  const briefing = status?.briefing;
  const versions = status?.versions ?? [];
  const selectedVersionId = viewedId || briefing?.id;
  const displayedBriefing =
    !selectedVersionId || selectedVersionId === briefing?.id
      ? briefing
      : draftCache[selectedVersionId] || briefing;
  const composerPrompts = latestChatPrompts(
    conversation?.messages,
    displayedBriefing
  );
  const thinking = generating && !error;
  const hasChat = (conversation?.messages ?? []).length > 0;
  const viewingOfficial =
    displayedBriefing?.id &&
    briefing?.id &&
    displayedBriefing.id === briefing.id &&
    displayedBriefing.visibility !== 'draft';
  const lastReadyAssistant = [...(conversation?.messages ?? [])]
    .reverse()
    .find(
      (message) => message.role === 'assistant' && message.status === 'ready'
    );
  const messageHasOffer = (conversation?.messages ?? []).some(
    (message) => message.pending_regenerate
  );

  const startBundleDownload = useCallback(
    (includeConversation) => {
      if (!downloadHref) {
        return;
      }
      const href = includeConversation
        ? `${downloadHref}&include_conversation=true`
        : downloadHref;
      dispatch(downloadFile(href, {}));
      setBundlePromptOpen(false);
    },
    [dispatch, downloadHref]
  );

  const onDownloadClick = useCallback(() => {
    if ((conversation?.messages ?? []).length > 0) {
      setBundleIncludeConversation(false);
      setBundlePromptOpen(true);
    } else {
      startBundleDownload(false);
    }
  }, [conversation, startBundleDownload]);

  const appendToComposer = useCallback((text) => {
    composerRef.current?.appendPrompt(text);
  }, []);

  const selectVersion = useCallback(
    async (briefingId) => {
      setViewedId(briefingId);
      if (
        !briefingId ||
        briefingId === briefing?.id ||
        draftCache[briefingId]
      ) {
        return;
      }

      try {
        const resp = await request.get(`${basePath}/drafts/${briefingId}`, {
          headers,
          params: query,
        });
        const next = resp.data?.data?.briefing;
        if (next) {
          setDraftCache((prev) => ({ ...prev, [briefingId]: next }));
        }
      } catch (e) {
        setError(requestErrorMessage(e, ANALYSIS_ERRORS.loadVersion));
      }
    },
    [basePath, briefing?.id, draftCache, headers, query]
  );

  // A missing provider key in this environment reads, to the reader, the same
  // as a plan without AI analysis: either way it cannot be used here.
  const aiUnavailable = status?.ai_available === false;
  const usable = canAnalyze && !aiUnavailable;
  // Nothing to analyze until a team in this org has results.
  const noResults = status?.has_results === false;

  const planRequired = (
    <div
      className="media text-muted mt-4 mb-4"
      data-test="org-analysis-unavailable"
    >
      <EmptyAnalysisIcon />
      <div className="media-body">
        <p>Generating an analysis briefing requires a plan with AI analysis.</p>
        <p>
          You can still download the results pack for these results and use your
          own AI to generate an analysis.
        </p>
      </div>
    </div>
  );

  return (
    <Card data-test="org-insights">
      <CardBody className={styles.analyze}>
        <div className={styles.header}>
          {usable ? <AnalysisUnitsGauge usage={status?.usage} /> : null}
          {downloadHref && !noResults ? (
            <button
              type="button"
              className={styles.actionChip}
              onClick={onDownloadClick}
              data-test="org-download-bundle"
            >
              <Icon icon={faCloudArrowDown} />
              <span>Download results</span>
            </button>
          ) : null}
        </div>

        {error ? <Alert color="danger">{error}</Alert> : null}

        {!canAnalyze ? (
          planRequired
        ) : loading ? (
          <LoadingMessage iconSize="2x">Loading insights…</LoadingMessage>
        ) : aiUnavailable ? (
          planRequired
        ) : (
          <>
            {status?.stale ? (
              <Alert color="warning">
                Scores have changed since this analysis
                {briefing?.inserted_at
                  ? ` (${new Date(briefing.inserted_at).toLocaleString()})`
                  : ''}
                . Refresh?
                <div className="mt-2">
                  {thinking ? (
                    <ThinkingIndicator stage={generateProgress} />
                  ) : (
                    <Button color="warning" size="sm" onClick={generate}>
                      Refresh analysis
                    </Button>
                  )}
                </div>
              </Alert>
            ) : null}

            {!briefing && noResults ? (
              <div
                className="media text-muted mt-4 mb-4"
                data-test="org-analysis-no-results"
              >
                <EmptyAnalysisIcon />
                <div className="media-body">
                  <p>There are no results to analyze yet.</p>
                  <p>
                    None of the teams here have completed this assessment. Once
                    a team has results, you can generate an analysis of them
                    here.
                  </p>
                </div>
              </div>
            ) : !briefing && thinking && partialBriefing ? (
              <OrgBriefing
                streamingDocument={partialBriefing}
                generating
                thinkingStage={generateProgress}
                onRegenerate={null}
                assessmentTemplate={assessmentTemplate}
                isRubric={isRubric}
              />
            ) : !briefing ? (
              <div className="media text-muted mt-4 mb-4">
                <EmptyAnalysisIcon />
                <div className="media-body">
                  {thinking ? (
                    <>
                      <p>Working on your analysis. This might take a moment.</p>
                      <ThinkingIndicator stage={generateProgress} />
                    </>
                  ) : (
                    <>
                      <p>Would you like an analysis of these results?</p>
                      <p>
                        The analyst will read the scores, history, and comments,
                        then write a briefing you can refine in chat.
                      </p>
                      <Button color="primary" onClick={generate}>
                        <Icon icon={faRobot} className="icon-left" />
                        Generate analysis
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                <OrgBriefing
                  briefing={displayedBriefing}
                  streamingDocument={thinking ? partialBriefing : null}
                  generating={thinking}
                  thinkingStage={generateProgress}
                  assessmentTemplate={assessmentTemplate}
                  isRubric={isRubric}
                  onRegenerate={!hasChat ? generate : null}
                  onPublish={
                    !viewingOfficial && displayedBriefing?.id
                      ? () => promoteDraft(displayedBriefing.id)
                      : null
                  }
                  publishing={promotingId === displayedBriefing?.id}
                  versions={versions}
                  selectedVersionId={selectedVersionId}
                  onSelectVersion={selectVersion}
                  currentUserId={currentUserId}
                />

                <RefineQuestions
                  questions={displayedBriefing?.document?.refine_questions}
                  collapsible={(conversation.messages ?? []).length > 0}
                />

                {(conversation.messages ?? []).length > 0 ? (
                  <div className={styles.thread}>
                    {/* The offer belongs to the turn that raised it. Fall
                        back to the last ready assistant for older threads
                        that only stored it on the conversation. */}
                    {/* A shared Generate/Refresh replaces the published
                        briefing rather than producing a private draft, but the
                        reader is usually watching the chat -- so its progress
                        shows here too, not only above the briefing. */}
                    {thinking ? (
                      <div
                        className={`${styles.message} ${styles.messageAssistant}`}
                      >
                        <ThinkingIndicator stage={generateProgress} />
                      </div>
                    ) : null}
                    {conversation.messages.map((item, index) => {
                      const draftPart = draftPartOf(item);
                      const draftId = draftPart?.ref?.briefing_id;
                      const expanded = draftId
                        ? expandedDrafts.has(draftId)
                        : false;
                      const live = item.generation
                        ? liveGenerations[item.generation.nonce]
                        : null;

                      return (
                        <div
                          key={item.id || `${item.inserted_at}-${index}`}
                          className={`${styles.message} ${
                            // eslint-disable-next-line no-nested-ternary
                            item.kind === 'action'
                              ? styles.messageAction
                              : item.role === 'assistant'
                              ? styles.messageAssistant
                              : styles.messageUser
                          }`}
                        >
                          {item.role === 'assistant' ? (
                            <MessageParts
                              item={item}
                              sending={sending}
                              onOffer={(label) =>
                                sendMessage(
                                  `Yes, regenerate with that: ${label}`
                                )
                              }
                              renderDraft={() =>
                                draftId ? (
                                  <DraftCard
                                    item={item}
                                    part={draftPart}
                                    full={draftCache[draftId]}
                                    expanded={expanded}
                                    promoting={promotingId === draftId}
                                    stage={live?.stage}
                                    partial={live?.partial}
                                    error={live?.error || item.error}
                                    onExpand={() => expandDraft(draftId)}
                                    onPromote={() => promoteDraft(draftId)}
                                    currentUserId={currentUserId}
                                    assessmentTemplate={assessmentTemplate}
                                    isRubric={isRubric}
                                  />
                                ) : null
                              }
                            />
                          ) : item.kind === 'action' ? null : (
                            item.content
                          )}

                          {item.role === 'assistant' &&
                          item.status === 'generating' &&
                          !draftId ? (
                            <>
                              {live?.text ? (
                                <ChatMarkdown text={live.text} />
                              ) : null}
                              <ThinkingIndicator stage={live?.stage} />
                            </>
                          ) : null}

                          {item.role === 'assistant' &&
                          item.status === 'failed' &&
                          !draftId ? (
                            <div className="text-danger" role="alert">
                              {friendlyReason(
                                live?.error || item.error,
                                ANALYSIS_ERRORS.replyFailed
                              )}
                            </div>
                          ) : null}

                          {item.kind === 'action' ? (
                            <span data-test="org-action-message">
                              {item.label || 'Requested an update'}
                            </span>
                          ) : null}

                          {item.role === 'assistant' ? (
                            <MessageActions
                              item={item}
                              prompts={item.chat_prompts}
                              onPick={appendToComposer}
                              sending={sending}
                              regenerating={regeneratingId === item.id}
                              onRegenerateResponse={() =>
                                regenerateResponse(item.id)
                              }
                              pendingRegenerate={
                                item.pending_regenerate ||
                                (!messageHasOffer &&
                                item.id === lastReadyAssistant?.id
                                  ? conversation.pending_regenerate
                                  : null)
                              }
                              updating={sending || thinking}
                              onRegenerateBriefing={requestBriefingUpdate}
                            />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                <p className={styles.privacyNote}>
                  Conversations with the analyst are only seen by you.
                </p>

                <ChatComposer
                  ref={composerRef}
                  disabled={sending}
                  sending={sending}
                  prompts={composerPrompts}
                  onSend={sendMessage}
                  onRegenerate={requestBriefingUpdate}
                  regenerating={sending}
                />
              </>
            )}
          </>
        )}
        <Modal
          isOpen={bundlePromptOpen}
          toggle={() => setBundlePromptOpen(false)}
          data-test="org-bundle-prompt"
        >
          <ModalHeader
            toggle={() => setBundlePromptOpen(false)}
            tag="div"
            className={styles.bundlePromptHeader}
          >
            What should be included in the export?
          </ModalHeader>
          <ModalBody>
            <RadioOptionGroup>
              <RadioOption
                name="org-bundle-contents"
                value="data"
                checked={!bundleIncludeConversation}
                onChange={() => setBundleIncludeConversation(false)}
                title="Data and analysis only"
                description={
                  <>
                    The assessment, every team&rsquo;s scores and history, and
                    the written analysis.
                  </>
                }
              />
              <RadioOption
                name="org-bundle-contents"
                value="conversation"
                checked={bundleIncludeConversation}
                onChange={() => setBundleIncludeConversation(true)}
                title="Data, analysis and conversation"
                description="Also includes your conversation. Conversations may contain private or sensitive information that isn’t part of the assessment data."
              />
            </RadioOptionGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="link" onClick={() => setBundlePromptOpen(false)}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => startBundleDownload(bundleIncludeConversation)}
            >
              Download
            </Button>
          </ModalFooter>
        </Modal>
      </CardBody>
    </Card>
  );
}
